import { useCallback, useEffect, type MutableRefObject } from 'react';
import { supabase } from '@/lib/supabase';
import type { WebrtcCallUiPhase } from '@/lib/telnyxCallState';

/**
 * Si el servidor marca al agente como `available` (colgó el remoto u otro cliente),
 * limpia la llamada WebRTC local.
 *
 * H1 C6: el listener Realtime se movió a `useAgentRealtimeChannel` (channel consolidado).
 * H1 C7: polling como FALLBACK adaptativo:
 *   - Default 2000ms (era 400ms = 150 req/min/agente = 15M req/min con 100k agentes)
 *   - Skip si Realtime channel está SUBSCRIBED (source of truth)
 *   - Skip si tab oculto (Visibility API)
 *   - Skip si no hay call en curso
 */
const POLLING_INTERVAL_MS = 2000;

export function useCallCenterAgentRemoteSync(params: {
  tenantId: string | null | undefined;
  userId: string | null | undefined;
  webrtcCallPhase: WebrtcCallUiPhase;
  isRealtimeConnected: boolean;
  clearActiveCallFromRemote: () => void;
  setCallStartTime: (t: number | null) => void;
  showRemoteHangupToast: () => void;
  lastHangupByUsAtRef: MutableRefObject<number>;
}): void {
  const {
    tenantId,
    userId,
    webrtcCallPhase,
    isRealtimeConnected,
    clearActiveCallFromRemote,
    setCallStartTime,
    showRemoteHangupToast,
    lastHangupByUsAtRef,
  } = params;

  const applyRemoteAvailable = useCallback(() => {
    clearActiveCallFromRemote();
    setCallStartTime(null);
    const justHungUpByUs = Date.now() - lastHangupByUsAtRef.current < 5000;
    if (!justHungUpByUs) showRemoteHangupToast();
  }, [clearActiveCallFromRemote, setCallStartTime, showRemoteHangupToast, lastHangupByUsAtRef]);

  useEffect(() => {
    if (!tenantId || !userId || webrtcCallPhase === 'none') return;
    if (isRealtimeConnected) return;

    const syncStatus = async () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      const { data } = await supabase
        .from('call_center_agent_status')
        .select('status')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .maybeSingle();
      if ((data as { status?: string } | null)?.status === 'available') {
        applyRemoteAvailable();
      }
    };
    const t = setInterval(syncStatus, POLLING_INTERVAL_MS);
    return () => clearInterval(t);
  }, [tenantId, userId, webrtcCallPhase, isRealtimeConnected, applyRemoteAvailable]);
}
